import {
  useIsMobile,
  WSAvatar,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSTabsNavigation,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { Alerts } from "../../../../shared/components/Alerts";
import {
  DataView,
  DataViewProps,
  DataViewSection
} from "../../../../shared/components/DataView";
import { useLocationStateStorage } from "../../../../shared/hooks/useLocationStateStorage";
import { selectorIsRequirementNotExpiring } from "../../selectors/selectorIsRequirementNotExpiring";
import { selectorIsSharedFileNotNoneContext } from "../../selectors/selectorIsSharedFileNotNoneContext";
import { selectorIsSharedFileRequest } from "../../selectors/selectorIsSharedFileRequest";
import { selectorRequirementWithReplacedSnapshot } from "../../selectors/selectorRequirementWithReplacedSnapshot";
import {
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../types";
import { RequirementsConfigResult } from "../../useRequirementsConfig";
import { LabelRequirementType } from "../LabelRequirementType";
import { LablePayeeName } from "../LablePayeeName";
import { LabelPayerName } from "../LablePayerName";

export type RequirementDetailsContentProps = RequirementsConfigResult & {
  contextType: RequirementContextType;
  onSuccessAction?: RequirementTableCallback;
  customContext?: ContextItem;
  requirement: PayeeRequirement;
  showTitle?: boolean;
};

/**
 * Shimmers one region of the page, and only when it has something to shimmer.
 *
 * `WSElement`'s shimmer hides its children (`opacity: 0`) and takes their size,
 * so wrapping an empty node yields a zero-height artifact and wrapping the whole
 * page yields a single collapsed grey bar instead of a page-shaped skeleton.
 * Applied per region, each block keeps the height of the content it is standing
 * in for.
 */
const Shimmerable: React.FC<{
  shimmer: boolean;
  children: React.ReactNode;
}> = ({ shimmer, children }) =>
  children ? <WSElement shimmer={shimmer}>{children}</WSElement> : null;

export const RequirementDetailsContent: React.FC<
  RequirementDetailsContentProps
> = ({
  requirement: _requirement,
  contextType,
  isLoading,
  isFetching,
  getRequirementName,
  getRequirementStatusAlert,
  getRequirementExpirationAlerts,
  getRequirementBasicInformation,
  getRequirementDetails,
  getRequirementDetailsWidgets,
  getRequirementDetailsProgressWidget,
  getRequirementDetailsHistoryWidget,
  getAdditionalData,
  getRequirementAdditionalContent,
  getRequirementExpirationWidget,
  getRequirementPayerName,
  getStatusCellProps,
  getTypeCellProps,
  onSuccessAction,
  showTitle = true
}) => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useLocationStateStorage(
    `requirement-details-tab-${location.pathname}`,
    "renewal"
  );

  const isRenewalAvailable = !!_requirement.replacedRequirement;
  const isEarlyRenewal =
    !!_requirement.replacedRequirement &&
    selectorIsRequirementNotExpiring(_requirement);

  const isExpiringTab = isRenewalAvailable
    ? currentTab === "expiring-soon"
    : false;

  const requirement = useMemo(() => {
    return isExpiringTab
      ? selectorRequirementWithReplacedSnapshot(_requirement)
      : _requirement;
  }, [_requirement, isExpiringTab]);

  const isMobile = useIsMobile();
  const statusAlertProps = getRequirementStatusAlert(_requirement);
  const expirationAlertProps = getRequirementExpirationAlerts(_requirement);
  const renewalStatusCellProps = getStatusCellProps(_requirement);

  const requirementBasicInformation =
    getRequirementBasicInformation(
      isExpiringTab ? { ...requirement, expiresAt: undefined } : requirement
    ) || [];
  const requirementDetails = getRequirementDetails(requirement);
  const requirementDetailsWidgets = getRequirementDetailsWidgets(requirement);
  const requirementName = getRequirementName(requirement);
  const progressWidget = getRequirementDetailsProgressWidget(requirement);
  const historyWidget = getRequirementDetailsHistoryWidget(requirement);
  const expirationWidget = getRequirementExpirationWidget(requirement);
  const additionalData = getAdditionalData(requirement);
  const additionalDetailsContent = getRequirementAdditionalContent(requirement);

  /**
   * Everything whose value comes from the data-source document is stale the
   * moment a fulfilment invalidates the cache, so it shimmers on a refresh as
   * well as on the first load. The identity block (name, client, type) does not:
   * it is unaffected by a status change, and blanking it on every refresh makes
   * the page flash.
   */
  const isStatusStale = isLoading || isFetching;

  const alerts = [statusAlertProps, ...expirationAlertProps];

  return (
    <WSElement>
      <WSList gap="XL">
        <Shimmerable shimmer={isLoading}>
          <WSFlexBox.CenterY gap="L">
            {showTitle && <WSText.Heading2>{requirementName}</WSText.Heading2>}
            {contextType === RequirementContextType.Payee &&
            selectorIsSharedFileRequest(additionalData) &&
            selectorIsSharedFileNotNoneContext(additionalData.contextType) ? (
              <WSAvatar
                colorBackground="garnet"
                format="square"
                icon="compass"
                size="L"
                type="icon"
              />
            ) : null}
          </WSFlexBox.CenterY>

          <WSFlexBox alignItems="center" gap="L" mt="S">
            {contextType === RequirementContextType.Payee ? (
              <LablePayeeName payeeId={requirement.payeeAccountId!} />
            ) : contextType === RequirementContextType.Payer ? (
              <LabelPayerName name={getRequirementPayerName(requirement)} />
            ) : null}

            <LabelRequirementType {...getTypeCellProps(requirement)} />
          </WSFlexBox>
        </Shimmerable>

        <Shimmerable shimmer={isStatusStale}>
          {/* `Alerts` renders null when every entry is absent; checked here too
              so the shimmer wrapper isn't an empty grey artifact. */}
          {alerts.filter(Boolean).length ? <Alerts alerts={alerts} /> : null}
        </Shimmerable>

        {isRenewalAvailable ? (
          <WSTabsNavigation
            activeIndex={currentTab === "renewal" ? 0 : 1}
            tabs={[
              {
                label: "Renewal",
                onClick() {
                  setCurrentTab("renewal");
                },
                pill: renewalStatusCellProps.pill?.icon
                  ? {
                      icon: renewalStatusCellProps.pill.icon,
                      theme: renewalStatusCellProps.pill.theme,
                      text: undefined
                    }
                  : undefined
              },
              {
                label: isEarlyRenewal ? "Current" : "Expiring soon",
                onClick() {
                  setCurrentTab("expiring-soon");
                }
              }
            ]}
          />
        ) : null}

        <WSGrid>
          <WSGrid.Item span={{ l: "8" }}>
            <WSList gap="L">
              <Shimmerable shimmer={isStatusStale}>
                {isMobile ? progressWidget : null}
              </Shimmerable>
              <Shimmerable shimmer={isStatusStale}>
                <DataViewSection
                  data={requirementBasicInformation}
                  gridColumns={3}
                  title="Requirement information"
                />
              </Shimmerable>
              <Shimmerable shimmer={isStatusStale}>
                {requirementDetails ? (
                  React.isValidElement(requirementDetails) ? (
                    requirementDetails
                  ) : (
                    <DataView {...(requirementDetails as DataViewProps)} />
                  )
                ) : null}
              </Shimmerable>
              {additionalDetailsContent ? additionalDetailsContent : null}
            </WSList>
          </WSGrid.Item>
          <WSGrid.Item span={{ l: "4" }}>
            <WSList gap="L">
              <Shimmerable shimmer={isStatusStale}>
                {isMobile ? null : progressWidget}
              </Shimmerable>
              <Shimmerable shimmer={isStatusStale}>
                {expirationWidget}
              </Shimmerable>
              <Shimmerable shimmer={isStatusStale}>
                {requirementDetailsWidgets}
              </Shimmerable>
              {historyWidget}
            </WSList>
          </WSGrid.Item>
        </WSGrid>
      </WSList>
    </WSElement>
  );
};
